import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Facebook, ArrowRight, Calendar, Tag, X, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { FACEBOOK_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";
import { BLOG } from "@/constants/testIds";
import { sanitizeHtml, looksLikeHtml } from "@/utils/sanitize";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };

function ArticleModal({ article, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (article && overlayRef.current) {
      overlayRef.current.scrollTop = 0;
    }
  }, [article]);

  if (!article) return null;
  const body = article.body || "";

  const handleShare = async () => {
    const url = `${window.location.origin}/api/cikk/${encodeURIComponent(article.key)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link a vágólapra másolva!");
    } catch (e) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link a vágólapra másolva!");
      } catch {
        toast.error("A link másolása nem sikerült.");
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-[#1A2825]/50 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
        data-testid={BLOG.articleModal || "blog-article-modal"}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-3xl bg-white rounded-[2rem] border border-[#EAE5D9] shadow-[0_30px_80px_-30px_rgba(28,107,97,0.45)] my-8 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {article.cover && (
            <div className="aspect-[21/9] overflow-hidden bg-[#F6F3EB]">
              <img src={article.cover} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
          )}
          <button
            type="button"
            data-testid="blog-article-modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/95 hover:bg-white flex items-center justify-center text-[#1A2825] shadow-md"
            aria-label="Bezár"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="p-6 md:p-10">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">
              <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> {article.category}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {article.date}</span>
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-snug">{article.title}</h2>
            {article.excerpt && (
              <p className="mt-4 text-[#A88A2B] italic text-base md:text-lg font-light leading-relaxed">
                {article.excerpt}
              </p>
            )}
            <div className="mt-6 gold-rule" />
            <div
              data-testid="blog-article-body"
              className="mt-6 text-[#3A4D4A] font-light leading-relaxed text-[16px] md:text-[17px] whitespace-pre-line prose-anchor"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
            />
            {article.ctaHref && article.ctaLabel && (
              (article.ctaHref.startsWith("http") || article.ctaHref.startsWith("mailto:")) ? (
                <a
                  href={article.ctaHref}
                  target={article.ctaHref.startsWith("http") ? "_blank" : undefined}
                  rel={article.ctaHref.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid="blog-article-cta"
                  className="mt-8 inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#1C6B61] text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)] hover:-translate-y-0.5"
                >
                  <Sparkles className="h-4 w-4" /> {article.ctaLabel}
                </a>
              ) : (
                <Link
                  to={article.ctaHref}
                  onClick={onClose}
                  data-testid="blog-article-cta"
                  className="mt-8 inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#1C6B61] text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)] hover:-translate-y-0.5"
                >
                  <Sparkles className="h-4 w-4" /> {article.ctaLabel}
                </Link>
              )
            )}
            <div className="mt-8 pt-6 border-t border-[#EAE5D9] flex items-center justify-between gap-3 flex-wrap">
              <span className="text-sm text-[#758C88] font-light">Tetszett a cikk? Oszd meg másokkal is.</span>
              <button
                type="button"
                onClick={handleShare}
                data-testid="blog-article-share"
                className="inline-flex items-center gap-2 bg-[#F6F3EB] hover:bg-[#EFE9DB] border border-[#EAE5D9] text-[#1C6B61] px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
              >
                <Share2 className="h-4 w-4" /> Megosztom
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Blog() {
  const { content } = useSiteContent();
  const BLOG_ARTICLES = content.articles;
  const BLOG_VIDEOS = content.videos;
  const [openArticle, setOpenArticle] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const key = new URLSearchParams(location.search).get("article");
    if (!key || !BLOG_ARTICLES.length) return;
    const found = BLOG_ARTICLES.find((a) => a.key === key);
    if (found && found.body && found.body.trim()) {
      setOpenArticle(found);
    }
  }, [location.search, content.articles]);

  return (
    <div data-testid={BLOG.page}>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10 md:pb-14">
        <div className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">Blog & Videók</span>
          <h1 className="font-serif-display text-5xl md:text-6xl font-light text-[#1A2825] mt-4 leading-[1.04]">
            Inspiráció, <em className="not-italic text-[#2A9D8F]">tippek</em>, mozgás.
          </h1>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            Friss írások a masszázsról, 4Life megoldásokról és a mindennapi rituálékról.
            A mozgásos videóim a Facebook-oldalamon élnek — egy kattintással megnézheted őket.
          </p>
        </div>
      </section>

      {/* Articles */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] leading-tight">Cikkek</h2>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B]">Frissül</div>
        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {BLOG_ARTICLES.map((a) => {
            const hasBody = !!(a.body && a.body.trim());
            return (
              <motion.article
                key={a.key}
                variants={fadeUp}
                data-testid={BLOG.articleCard(a.key)}
                className="group relative bg-white rounded-[1.75rem] border border-[#EAE5D9] overflow-hidden flex flex-col hover:-translate-y-1 transition-transform"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#F6F3EB]">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">
                    <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> {a.category}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {a.date}</span>
                  </div>
                  <h3 className="font-serif-display text-xl md:text-2xl text-[#1A2825] mt-3 leading-snug">{a.title}</h3>
                  {looksLikeHtml(a.excerpt) ? (
                    <div
                      className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed flex-1 whitespace-pre-line prose-anchor line-clamp-4"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.excerpt) }}
                    />
                  ) : (
                    <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed flex-1 whitespace-pre-line line-clamp-4">
                      {a.excerpt}
                    </p>
                  )}
                  {hasBody ? (
                    <button
                      type="button"
                      onClick={() => setOpenArticle(a)}
                      data-testid={`blog-article-read-${a.key}`}
                      className="mt-5 self-start inline-flex items-center gap-2 bg-[#2A9D8F] hover:bg-[#1C6B61] text-white px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all shadow-[0_6px_18px_rgba(42,157,143,0.25)] hover:-translate-y-0.5"
                    >
                      Tovább olvasom <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#758C88] italic">
                      Hamarosan olvasható
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </section>

      {openArticle && <ArticleModal article={openArticle} onClose={() => setOpenArticle(null)} />}

      {/* Videos */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] leading-tight">Videók a Facebookon</h2>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#1877F2] hover:text-[#0e5fcc]"
          >
            <Facebook className="h-3.5 w-3.5" /> Teljes lista
          </a>
        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {BLOG_VIDEOS.map((v) => {
            const videoUrl = (v.facebook_url && v.facebook_url.trim()) || FACEBOOK_URL;
            return (
            <motion.article
              key={v.key}
              variants={fadeUp}
              data-testid={BLOG.videoCard(v.key)}
              className="relative bg-white rounded-[1.75rem] border border-[#EAE5D9] overflow-hidden flex flex-col"
            >
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="relative aspect-video block overflow-hidden bg-[#1A2825] group"
              >
                <img
                  src={v.cover}
                  alt={v.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                    <Play className="h-6 w-6 text-[#1877F2] ml-1" fill="#1877F2" />
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur text-white text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">
                  {v.duration}
                </div>
              </a>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-serif-display text-xl text-[#1A2825] leading-snug">{v.title}</h3>
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={BLOG.fbVideoBtn(v.key)}
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all hover:-translate-y-0.5"
                >
                  <Facebook className="h-4 w-4" />
                  Megnézem a videót a Facebookon
                </a>
                {v.articleKey && (
                  <Link
                    to={`/blog?article=${encodeURIComponent(v.articleKey)}`}
                    data-testid={`video-article-link-${v.key}`}
                    className="mt-2 inline-flex items-center justify-center gap-2 text-[#1C6B61] hover:text-[#2A9D8F] text-sm font-medium transition-colors"
                  >
                    Elolvasom a cikket <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </motion.article>
            );
          })}
        </motion.div>
      </section>

      {/* Embed-ready notice for the user */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="bg-[#F6F3EB]/80 border border-[#D4AF37]/30 rounded-[2.5rem] p-8 md:p-12 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B]">Beágyazás-kész</div>
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#1A2825] mt-2 leading-tight">
            A videók közvetlen Facebook-beágyazásra is alkalmasak.
          </h3>
          <p className="mt-3 text-[#3A4D4A] font-light leading-relaxed max-w-2xl mx-auto">
            Amikor megosztod a videók linkjeit, automatikusan beágyazhatóak a kártyák helyére.
            Addig is a látogatók egy kattintással a Facebook-oldaladon nézhetik meg őket.
          </p>
        </div>
      </section>
    </div>
  );
}
